// 📁 src/pages/MVPAgent.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "../utils/axiosInstance";
import {
  PlayCircle,
  Download,
  ArrowRight,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle,
  Zap,
  History,
  FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useIdea } from "../context/IdeaContext";
import IdeaPopup from "./IdeaPopup";
import buildMDIHPdfName from "../utils/pdfNaming";
import ModuleHeader from "../components/ModuleHeader";

/* ================================================================
   Loading step messages shown during generation
================================================================ */
const LOADING_STEPS = [
  "Analysing your solution...",
  "Identifying components and build phases...",
  "Estimating costs and timelines...",
  "Generating step-by-step execution plan...",
  "Validating and finalising plan...",
];

/* ================================================================
   resolveField — picks the first non-empty, non-placeholder value
   from a list of candidates. Used to make MVPAgent independent
   of whether MVPPlanner has been run in the current session.
================================================================ */
const EMPTY_MARKERS = [
  "", "describe the problem", "describe your solution",
  "target user", "not specified", "n/a",
];

function resolveField(...candidates) {
  for (const val of candidates) {
    const v = (val || "").trim();
    if (v && !EMPTY_MARKERS.includes(v.toLowerCase())) return v;
  }
  return "";
}

const MVPAgent = () => {
  const navigate = useNavigate();
  const {
    userId: contextUserId,
    selectedIdea,
    painPoint,
    proposedSolution,
    targetUser: contextTargetUser,
    userProfile,
    businessModel,
    completedModules,   // ← used to read MVPPlanner output without running it
    markModuleComplete,
  } = useIdea();

  /* ── Startup name ─────────────────────────────────────────────── */
  const startupName =
    selectedIdea?.title ||
    selectedIdea?.ideaTitle ||
    businessModel?.startupName ||
    "Startup";

  const fallbackUserId = "64f5c1a0abc123def4567890";
  const userId = contextUserId || fallbackUserId;
  const [ideaId] = useState(selectedIdea?.ideaId || null);
  const [recordId, setRecordId] = useState(null);

  /* ================================================================
     FIX 2 — resolveFormData
     Pulls problem / solution / targetUser from ALL available sources
     in priority order so MVPAgent never needs MVPPlanner to have
     run in the same session:

     Priority:
       1. Live context values (set when MVPPlanner ran this session)
       2. completedModules.mvpPlanner (persisted from a previous session)
       3. selectedIdea fields (always available from idea selection)
       4. businessModel / userProfile fallbacks
  ================================================================ */
  const resolveFormData = () => {
    // MVPPlanner output persisted across sessions
    const planner = completedModules?.mvpPlanner || {};

    const problem = resolveField(
      painPoint,
      planner.problem,
      planner.painPoint,
      selectedIdea?.painPoint,
      selectedIdea?.problem,
      selectedIdea?.challenge,
      selectedIdea?.description,    // last resort — idea description
    );

    const solution = resolveField(
      proposedSolution,
      planner.solution,
      planner.proposedSolution,
      selectedIdea?.proposedSolution,
      selectedIdea?.solution,
      selectedIdea?.description,
    );

    const targetUser = resolveField(
      contextTargetUser,
      planner.targetUser,
      planner.targetAudience,
      selectedIdea?.targetUser,
      selectedIdea?.targetAudience,
      selectedIdea?.audience,
      businessModel?.targetSegment,
    );

    const ideaTitle = resolveField(
      selectedIdea?.ideaTitle,
      selectedIdea?.title,
      planner.ideaTitle,
      localStorage.getItem("activeIdeaTitle"),
    );

    return {
      ideaTitle,
      problem,
      solution,
      targetUser,
      currency: userProfile?.currency || "INR",
      userLocation: userProfile?.location || "India",
      productType: planner.productType || selectedIdea?.productType || "",
    };
  };

  const [formData, setFormData] = useState({
    ideaTitle: "",
    problem: "",
    solution: "",
    targetUser: "",
    currency: "INR",
    userLocation: "India",
    productType: "",
  });

  /* ── Plan state ──────────────────────────────────────────────── */
  const [mvpPlan, setMvpPlan]             = useState(null);   // FIX 1: starts null, never auto-set from DB
  const [loading, setLoading]             = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingStep, setLoadingStep]     = useState(0);
  // "newly_generated" | "restored_from_history" | null
  const [planStatus, setPlanStatus]       = useState(null);

  /* ── Previous plans list ─────────────────────────────────────── */
  const [previousPlans, setPreviousPlans]         = useState([]);
  const [showPreviousPlans, setShowPreviousPlans] = useState(false);
  const [loadingPrevious, setLoadingPrevious]     = useState(false);
  const [preflightDone, setPreflightDone]         = useState(false);

  /* ── Next actions ────────────────────────────────────────────── */
  const [nextActions, setNextActions] = useState({});
  const [loadingNext, setLoadingNext] = useState({});

  /* ── Popup ───────────────────────────────────────────────────── */
  const [showPopup, setShowPopup]       = useState(false);
  const [popupPayload, setPopupPayload] = useState(null);

  const planTopRef = useRef(null);

  /* ================================================================
     Persist active idea title
  ================================================================ */
  useEffect(() => {
    if (selectedIdea?.ideaTitle)
      localStorage.setItem("activeIdeaTitle", selectedIdea.ideaTitle);
  }, [selectedIdea]);

  /* ================================================================
     FIX 2 — Populate form from all available sources on mount
     and whenever context updates. No dependency on MVPPlanner
     having run in the current session.
  ================================================================ */
  useEffect(() => {
    const resolved = resolveFormData();
    setFormData((prev) => ({
      ...prev,
      ideaTitle:    resolved.ideaTitle    || prev.ideaTitle,
      problem:      resolved.problem      || prev.problem,
      solution:     resolved.solution     || prev.solution,
      targetUser:   resolved.targetUser   || prev.targetUser,
      currency:     resolved.currency     || prev.currency,
      userLocation: resolved.userLocation || prev.userLocation,
      productType:  resolved.productType  || prev.productType,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdea, painPoint, proposedSolution, contextTargetUser, userProfile, completedModules]);

  /* ================================================================
     FIX 1 — On mount: load the list of previous plans silently.
     Do NOT auto-display any plan. mvpPlan stays null until the
     user either generates a new plan or picks one from the list.
  ================================================================ */
  useEffect(() => {
    if (!userId) return;
    const loadList = async () => {
      setLoadingPrevious(true);
      try {
        const query = ideaId ? `?ideaId=${ideaId}` : "";
        const res = await axios.get(`/api/mvp-agent/my-plans${query}`);
        if (res.data?.plans) {
          // Show max 5 most recent plans
          setPreviousPlans(res.data.plans.slice(0, 5));
        }
      } catch (err) {
        console.warn("⚠️ Could not load previous plans:", err.message);
      } finally {
        setLoadingPrevious(false);
      }
    };
    loadList();
  }, [userId, ideaId]);

  /* ================================================================
     Preflight — runs silently after form data is ready.
     Pre-computes classify + DNA so generate-plan only runs
     the main generation call (~25s), avoiding the timeout.
  ================================================================ */
  useEffect(() => {
    if (!userId || !ideaId || preflightDone) return;

    const { solution, ideaTitle, currency, productType } = formData;
    if (!solution || !ideaTitle) return;
    // Don't run if form still has placeholder values
    if (EMPTY_MARKERS.includes(solution.toLowerCase())) return;

    const runPreflight = async () => {
      try {
        console.log("🚀 Silent preflight...");
        const res = await axios.post("/api/mvp-agent/preflight", {
          ideaId, ideaTitle, solution, productType, currency,
        }, { timeout: 30000 });
        if (res.data?.success) {
          setPreflightDone(true);
          console.log("✅ Preflight done:", res.data);
        }
      } catch (err) {
        console.warn("⚠️ Preflight failed (non-fatal):", err.message);
      }
    };

    runPreflight();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, ideaId, formData.solution, formData.ideaTitle]);

  /* ================================================================
     Loading step ticker
  ================================================================ */
  useEffect(() => {
    if (!loading) { setLoadingStep(0); return; }
    const interval = setInterval(() => {
      setLoadingStep((prev) => prev + 1 < LOADING_STEPS.length ? prev + 1 : prev);
    }, 6000);
    return () => clearInterval(interval);
  }, [loading]);

  /* ================================================================
     Restore a plan from the previous-plans list
  ================================================================ */
  const restorePlan = (doc) => {
    setMvpPlan(doc.plan);
    setRecordId(doc._id?.toString());
    setPlanStatus("restored_from_history");
    setNextActions({});
    setShowPreviousPlans(false);
    setTimeout(() => planTopRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  /* ================================================================
     Reload the list after a new plan is generated
  ================================================================ */
  const refreshPreviousList = async (excludeId) => {
    try {
      const query = ideaId ? `?ideaId=${ideaId}` : "";
      const res = await axios.get(`/api/mvp-agent/my-plans${query}`);
      if (res.data?.plans) {
        const all = res.data.plans.filter((p) => p._id?.toString() !== excludeId);
        setPreviousPlans(all.slice(0, 5));
      }
    } catch (err) {
      console.warn("⚠️ Refresh list failed:", err.message);
    }
  };

  /* ================================================================
     AI Motivation Popup
  ================================================================ */
  const showMotivation = async (context = "", overrideRecordId = null) => {
    let message = "Your plan is ready. Start executing step by step. Momentum builds results.";
    try {
      const res = await axios.post("/api/mvp-agent/ai-tip", {
        ideaId, recordId: overrideRecordId || recordId, context,
      });
      message = res.data?.tip || message;
    } catch (err) {
      console.error("❌ ai-tip:", err.message);
    }
    setPopupPayload({ idea: { title: "MVP Agent", description: context }, type: "feedback", aiMessageOverride: message });
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 3500);
  };

  /* ================================================================
     AI Next Action
  ================================================================ */
  const fetchNextAction = async (component, index) => {
    if (!component || nextActions[index]) return;
    setLoadingNext((p) => ({ ...p, [index]: true }));
    try {
      const context = `
Product Type: ${mvpPlan?.productType} | Target: ${mvpPlan?.targetAudience}
Task: ${component.name} — ${component.description}
Time: ${component.estimate?.time} | Tools: ${(component.frameworksOrSDKs || []).join(", ")}
Give ONE specific immediately-executable next action. Max 2 sentences.
`;
      const res = await axios.post("/api/mvp-agent/ai-tip", { ideaId, recordId, context });
      setNextActions((prev) => ({
        ...prev,
        [index]: res.data?.tip || "Break this task into smaller steps and start the first one.",
      }));
    } catch (err) {
      console.error("next action error:", err);
    } finally {
      setLoadingNext((p) => ({ ...p, [index]: false }));
    }
  };

  /* ================================================================
     Generate Plan
  ================================================================ */
  const generatePlan = async () => {
    // Guard: ensure we have the minimum required fields
    if (!formData.solution || EMPTY_MARKERS.includes(formData.solution.toLowerCase())) {
      alert("Please ensure your solution is defined before generating the plan.");
      return;
    }

    setLoading(true);
    setLoadingStep(0);
    try {
      const res = await axios.post(
        "/api/mvp-agent/generate-plan",
        { ...formData, ideaId, recordId, userId },
        { timeout: 120000 }
      );

      const generatedPlan = res.data?.mvpPlan?.plan || res.data?.plan;
      const newRecordId   = res.data?.recordId;

      setMvpPlan(generatedPlan);
      setRecordId(newRecordId);
      setPlanStatus("newly_generated");
      setNextActions({});
      setShowPreviousPlans(false);

      // Refresh previous list in background (excluding the new plan)
      refreshPreviousList(newRecordId);

      try {
        markModuleComplete("mvpAgent", {
          ideaId, recordId: newRecordId,
          plan: generatedPlan,
          problem: formData.problem,
          solution: formData.solution,
          targetUser: formData.targetUser,
          productType: generatedPlan?.productType || "",
        });
      } catch (err) {
        console.warn("⚠️ markModuleComplete failed:", err);
      }

      // Scroll to new plan
      setTimeout(() => planTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      showMotivation("Your execution plan is ready. Start with Step 1.", newRecordId);

    } catch (err) {
      console.error("❌ MVPAgent error:", err);
      alert("Failed to generate MVP plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ================================================================
     Helpers
  ================================================================ */
  const formatMilestones = (m) =>
    Object.entries(m || {}).map(([_, v], i) => ({
      title: `Milestone ${i + 1}`,
      description: v.description,
      dueDate: v.dueDate || "—",
    }));

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      });
    } catch { return dateStr; }
  };

  /* ================================================================
     Export PDF
  ================================================================ */
  const exportToPDF = async () => {
    if (!mvpPlan) return;
    try {
      setLoadingExport(true);
      const resolvedIdeaId = ideaId || selectedIdea?.ideaId || recordId;
      if (!resolvedIdeaId) { alert("Idea ID missing"); return; }

      const res = await axios.post(
        "/api/pdf/mvp-agent",
        { ideaId: resolvedIdeaId, recordId },
        { responseType: "blob" }
      );

      const url  = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href  = url;
      link.download = buildMDIHPdfName(startupName, "MVP_Execution");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("❌ PDF failed:", err);
      alert("Failed to download PDF");
    } finally {
      setLoadingExport(false);
    }
  };

  const proceedToAITesting = () =>
    navigate("/ai-testing", { state: { mvpPlan, mvpAgent: mvpPlan, ideaId } });

  /* ================================================================
     Derived: is the form ready to generate?
  ================================================================ */
  const isFormReady =
    formData.ideaTitle &&
    formData.solution &&
    !EMPTY_MARKERS.includes((formData.solution || "").toLowerCase());

  /* ================================================================
     UI
  ================================================================ */
  return (
    <div className="bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 pt-6 pb-12">

        <ModuleHeader
          title="MVP Agent"
          subtitle="Turn your MVP into an execution-ready build blueprint."
          currentModule="mvpAgent"
          isLoadedFromDB={false}  // never show "loaded from DB" — we don't auto-load
        />

        {/* Active idea */}
        <div className="text-sm text-gray-500 border-b border-slate-200 pb-4 mb-6 flex justify-between items-center">
          <span><b>Startup:</b> {startupName}</span>
          {preflightDone && (
            <span className="text-xs text-green-600 flex items-center gap-1">
              <Zap size={11} /> Ready to generate
            </span>
          )}
        </div>

        {/* ── FIX 2: Show resolved fields so user can verify ───── */}
        {isFormReady && (
          <div className="mb-6 bg-white border border-slate-200 rounded-xl px-6 py-4 text-sm text-slate-600 space-y-1">
            <div className="font-semibold text-slate-800 mb-2">Plan will be generated for:</div>
            <div><span className="text-slate-400 w-28 inline-block">Idea</span> {formData.ideaTitle}</div>
            <div><span className="text-slate-400 w-28 inline-block">Solution</span> {formData.solution.length > 120 ? formData.solution.slice(0, 120) + "…" : formData.solution}</div>
            <div><span className="text-slate-400 w-28 inline-block">Target User</span> {formData.targetUser || "—"}</div>
            <div><span className="text-slate-400 w-28 inline-block">Currency</span> {formData.currency}</div>
          </div>
        )}

        {/* ── FIX 2: Warning if form data is incomplete ─────────── */}
        {!isFormReady && (
          <div className="mb-6 px-6 py-4 rounded-xl bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm">
            <b>Solution data not yet available.</b> Please ensure your idea and solution are set up
            in the Ideas or MVPPlanner module before generating. If you have already done this,
            try refreshing the page.
          </div>
        )}

        {/* ── Generate CTA ───────────────────────────────────────── */}
        <div className="mb-6">
          <button
            onClick={generatePlan}
            disabled={loading || !isFormReady}
            className="relative inline-flex items-center gap-3 px-10 py-4
                       bg-gradient-to-r from-blue-600 to-indigo-600
                       hover:from-blue-700 hover:to-indigo-700
                       text-white font-semibold rounded-xl
                       shadow-lg hover:shadow-xl
                       transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlayCircle size={20} />
            {loading ? "Building Execution Plan..." : "Generate MVP Execution Plan"}
          </button>

          {loading && (
            <div className="mt-4 px-6 py-4 rounded-xl bg-blue-50 border border-blue-200 text-blue-800 text-sm space-y-3">
              <div className="flex items-center gap-2 font-medium">
                <Sparkles size={16} className="animate-pulse" />
                {LOADING_STEPS[loadingStep]}
              </div>
              <div className="w-full bg-blue-100 rounded-full h-1.5">
                <div
                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-1000"
                  style={{ width: `${((loadingStep + 1) / LOADING_STEPS.length) * 100}%` }}
                />
              </div>
              <div className="text-xs text-blue-600">
                This takes 20–30 seconds. Please keep this tab open.
              </div>
            </div>
          )}
        </div>

        {/* ── FIX 1: Previous Plans Section ─────────────────────── */}
        {/* Shown on the clean page before any plan is generated.   */}
        {/* Always available via the toggle button after generation. */}
        <div className="mb-8">
          <button
            onClick={() => setShowPreviousPlans((v) => !v)}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600
                       hover:text-slate-900 border border-slate-300 bg-white hover:bg-slate-50
                       px-4 py-2.5 rounded-lg transition"
          >
            <History size={15} />
            {previousPlans.length > 0
              ? `Previous Plans (${previousPlans.length})`
              : "No Previous Plans"}
            {showPreviousPlans ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {showPreviousPlans && (
            <div className="mt-3 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b flex justify-between items-center">
                <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <FileText size={14} /> Previously Generated Plans
                </span>
                <button
                  onClick={() => setShowPreviousPlans(false)}
                  className="text-xs text-slate-400 hover:text-slate-700"
                >
                  Close ✕
                </button>
              </div>

              {loadingPrevious ? (
                <div className="px-5 py-6 text-sm text-slate-400 flex items-center gap-2">
                  <Sparkles size={14} className="animate-pulse" /> Loading…
                </div>
              ) : previousPlans.length === 0 ? (
                <div className="px-5 py-6 text-sm text-slate-400">
                  No previously generated plans found. Generate your first plan above.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {previousPlans.map((doc, idx) => (
                    <li
                      key={doc._id?.toString() || idx}
                      className="px-5 py-4 flex justify-between items-center hover:bg-slate-50 transition"
                    >
                      <div>
                        <div className="text-sm font-medium text-slate-800">
                          {doc.plan?.productType || "MVP Plan"}
                          {doc.ideaTitle ? ` — ${doc.ideaTitle}` : ""}
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                          <span>
                            Total: <b className="text-slate-700">{doc.plan?.costEstimates?.total || "—"}</b>
                          </span>
                          <span>{doc.plan?.components?.length || 0} components</span>
                          <span>
                            <Clock size={10} className="inline mr-0.5" />
                            {formatDate(doc.updatedAt || doc.createdAt)}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => restorePlan(doc)}
                        className="shrink-0 ml-4 text-xs bg-indigo-600 hover:bg-indigo-700
                                   text-white px-4 py-1.5 rounded-lg font-medium transition"
                      >
                        Open Plan
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* ── Status banners (shown only after user action) ─────── */}
        {planStatus === "newly_generated" && (
          <div className="mb-6 px-6 py-3 rounded-lg bg-green-50 border border-green-300 text-green-800 text-sm flex items-center gap-2">
            <CheckCircle size={16} className="text-green-600 shrink-0" />
            New execution plan generated successfully.
          </div>
        )}

        {planStatus === "restored_from_history" && (
          <div className="mb-6 px-6 py-3 rounded-lg bg-indigo-50 border border-indigo-300 text-indigo-800 text-sm flex justify-between items-center">
            <span className="flex items-center gap-2">
              <History size={15} className="shrink-0" />
              Viewing a previously generated plan.
            </span>
            <button
              onClick={generatePlan}
              disabled={loading}
              className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-md disabled:opacity-60"
            >
              Generate New Plan
            </button>
          </div>
        )}

        {/* ── Plan anchor ───────────────────────────────────────── */}
        <div ref={planTopRef} />

        {/* ── Plan Output — only rendered after user action ─────── */}
        {mvpPlan && (
          <div className="mt-2">

            {/* SUMMARY */}
            <section className="bg-white border rounded-2xl px-10 py-12">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">Summary & Basics</h2>
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div className="space-y-1">
                  <div className="text-xs uppercase text-slate-500">Summary</div>
                  <div className="text-slate-700 leading-relaxed">
                    {mvpPlan.summary || `MVP execution plan for ${mvpPlan.targetAudience || "your target users"}.`}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs uppercase text-slate-500">Problem Statement</div>
                  <div className="text-slate-700">{mvpPlan.problemStatement || formData.problem}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs uppercase text-slate-500">Target Audience</div>
                  <div className="text-slate-700">{mvpPlan.targetAudience || formData.targetUser}</div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs uppercase text-slate-500">Product Type</div>
                  <div className="text-slate-700">{mvpPlan.productType || "—"}</div>
                </div>
              </div>

              <div className="border-t mt-6 pt-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Core MVP Features</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <div className="text-xs uppercase text-green-700 mb-2">Included</div>
                    <ul className="list-disc pl-5 space-y-1 text-slate-700">
                      {(mvpPlan.coreMvpFeatures?.included || []).map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-red-600 mb-2">Excluded</div>
                    <ul className="list-disc pl-5 space-y-1 text-slate-700">
                      {(mvpPlan.coreMvpFeatures?.excluded || []).map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* EXECUTION TASKS */}
            <section className="mt-10">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Execution Tasks</h2>
              <div className="space-y-6">
                {(mvpPlan.components || []).map((c, i) => (
                  <div key={i} className="bg-white border rounded-xl px-8 py-7 space-y-6">
                    <div className="flex justify-between items-start">
                      <h3 className="text-lg font-semibold text-slate-800">
                        Step {i + 1}: {c.name}
                      </h3>
                      <button
                        onClick={() => fetchNextAction(c, i)}
                        disabled={loadingNext[i]}
                        className="text-xs flex items-center gap-1 text-indigo-600 hover:underline shrink-0 ml-4"
                      >
                        <Sparkles size={14} />
                        {loadingNext[i] ? "Thinking…" : "Next Recommended Action"}
                      </button>
                    </div>

                    <p className="text-sm text-slate-600 leading-relaxed">{c.description}</p>

                    {c.implementationSteps?.length > 0 && (
                      <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                        <div className="text-xs font-semibold text-slate-500 uppercase">
                          Implementation Steps
                        </div>
                        <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-700">
                          {c.implementationSteps.map((step, idx) => <li key={idx}>{step}</li>)}
                        </ol>
                      </div>
                    )}

                    {c.validationChecks?.length > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
                        <div className="text-xs font-semibold text-green-700 uppercase">
                          Validation Checks
                        </div>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-green-900">
                          {c.validationChecks.map((check, idx) => <li key={idx}>{check}</li>)}
                        </ul>
                      </div>
                    )}

                    <div className="grid md:grid-cols-3 gap-4 text-sm bg-slate-50 rounded-lg p-4">
                      <div>
                        <span className="text-xs uppercase text-slate-500 block mb-1">Time</span>
                        <span className="font-medium text-slate-800">{c.estimate?.time || "—"}</span>
                      </div>
                      <div>
                        <span className="text-xs uppercase text-slate-500 block mb-1">Cost</span>
                        <span className="font-medium text-slate-800">{c.estimate?.cost || "—"}</span>
                      </div>
                      <div>
                        <span className="text-xs uppercase text-slate-500 block mb-1">Tools</span>
                        <span className="font-medium text-slate-800">
                          {(c.frameworksOrSDKs || []).join(", ") || "—"}
                        </span>
                      </div>
                    </div>

                    {nextActions[i] && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-1">
                          AI Recommended Next Action
                        </div>
                        <div className="text-sm text-indigo-900">{nextActions[i]}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* COST BREAKDOWN */}
            <section className="bg-white border rounded-xl px-8 py-8 mt-10">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Cost Breakdown</h2>
              <div className="flex justify-between bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
                <span className="font-semibold text-blue-900">Total Estimated Cost</span>
                <span className="font-bold text-blue-900 text-lg">
                  {mvpPlan.costEstimates?.total || "—"}
                </span>
              </div>
              {mvpPlan.costEstimates?.breakdown && (
                <div className="space-y-2">
                  {Object.entries(mvpPlan.costEstimates.breakdown).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm border-b pb-2">
                      <span className="text-slate-600 capitalize">{k.replace(/([A-Z])/g, " $1")}</span>
                      <span className="font-medium text-slate-800">{v}</span>
                    </div>
                  ))}
                </div>
              )}
              {mvpPlan.costExplanation?.note && (
                <p className="mt-4 text-xs text-slate-400 leading-relaxed">
                  {mvpPlan.costExplanation.note}
                </p>
              )}
            </section>

            {/* RISKS */}
            <section className="mt-10">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Risks & Mitigation</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {(mvpPlan.risksMitigation || []).map((r, i) => (
                  <div key={i} className="bg-white border-l-4 border-red-500 rounded-lg p-4">
                    <div className="font-semibold text-slate-800">{r.risk}</div>
                    {r.impact && (
                      <div className="flex gap-3 mt-1 text-xs text-slate-500">
                        <span>Impact: <b className="capitalize">{r.impact}</b></span>
                        <span>Likelihood: <b className="capitalize">{r.likelihood}</b></span>
                      </div>
                    )}
                    <div className="text-sm text-slate-600 mt-2">{r.mitigation}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* MILESTONES */}
            <section className="bg-white border rounded-xl px-8 py-8 mt-10">
              <h2 className="text-2xl font-bold text-blue-900 mb-4">Milestones</h2>
              <div className="space-y-3">
                {formatMilestones(mvpPlan.milestones).map((m, i) => (
                  <div key={i} className="flex justify-between items-center bg-slate-50 p-4 rounded-lg">
                    <div>
                      <div className="font-semibold text-slate-800">{m.title}</div>
                      <div className="text-sm text-slate-600">{m.description}</div>
                    </div>
                    <div className="text-sm text-slate-500 shrink-0 ml-4">Due: {m.dueDate}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* NEXT STEP */}
            <section className="grid md:grid-cols-2 gap-8 mt-12 pt-8 border-t">
              <div className="bg-white border rounded-2xl p-8 space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">Lock This Execution Plan</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Freeze your MVP execution blueprint and export it for sharing,
                  team alignment, or investor review.
                </p>
                <button
                  onClick={exportToPDF}
                  disabled={loadingExport}
                  className="mt-4 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg
                             font-semibold inline-flex items-center gap-2 transition disabled:opacity-60"
                >
                  <Download size={18} />
                  {loadingExport ? "Exporting…" : "Download Execution Plan →"}
                </button>
              </div>

              <div className="bg-white border rounded-2xl p-8 space-y-4">
                <h3 className="text-xl font-semibold text-slate-900">Ready to Validate This MVP?</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Stress-test assumptions and validate feasibility using AI-powered testing.
                </p>
                <button
                  onClick={proceedToAITesting}
                  className="mt-4 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-lg
                             font-semibold inline-flex items-center gap-2 transition"
                >
                  Go to AI Testing →
                  <ArrowRight size={18} />
                </button>
              </div>
            </section>

          </div>
        )}
      </div>

      {showPopup && popupPayload && (
        <div className="fixed bottom-5 right-5 z-50">
          <IdeaPopup {...popupPayload} autoClose />
        </div>
      )}
    </div>
  );
};

export default MVPAgent;
